import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useSetUserRole, useUpdateUser, useUser } from '../contexts/users';
import { toUpdatePayload, validate, valuesFrom } from '../utils/form';
import UserFields from './fields';

import '../../clients/styles/form.css';

// Two components: the outer waits for the record, the inner owns the form and
// seeds its state once from what it receives. No effect copies the record into
// state — the `key` remounts the form if the id changes.

export default function EditUserForm({ userId }) {
    const { data: user, isLoading, error } = useUser(userId);

    if (isLoading) {
        return <p className="client-form__loading">Cargando usuario...</p>;
    }

    if (error) {
        return (
            <p className="client-form__failure">
                {error.message || 'No se pudo cargar el usuario'}
            </p>
        );
    }

    return <UserEditor key={user.id} user={user} />;
}

function UserEditor({ user }) {
    const updateUser = useUpdateUser(user.id);
    const setRole = useSetUserRole(user.id);

    const [values, setValues] = useState(() => valuesFrom(user));
    const [errors, setErrors] = useState({});

    const saving = updateUser.isPending || setRole.isPending;

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values, { withPassword: false });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            await updateUser.mutateAsync(toUpdatePayload(values));

            // The role goes through its own route, and only if it actually
            // changed: sending it every time would demand `users:admin` from
            // somebody who is just fixing a typo in a name.
            if (values.role !== user.role) {
                await setRole.mutateAsync(values.role);
            }

            toast.success('Usuario guardado');
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo guardar el usuario');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">{user.name}</h2>
                <p className="client-form__subtitle">
                    Usuario #{user.id} · {user.email}
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos de la cuenta</h3>
                        <p className="client-form__section-text">
                            La contraseña no se edita desde acá: la cambia cada persona
                            desde su propia cuenta.
                        </p>
                    </div>

                    <UserFields
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={saving}
                        withPassword={false}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/people/usuarios" className="client-form__cancel">Volver</Link>
                    <button
                        type="submit"
                        className="client-form__submit"
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar cambios'}
                    </button>
                </div>
            </form>
        </section>
    );
}
